/**
* @summary - Component that Provides Login functionality
* @autor vengateshwaran k
* @since 2024-05-30
*/
import styled from "@emotion/styled";
import { yupResolver } from "@hookform/resolvers/yup";
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  Link,
  TextField,
  Typography
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import CryptoJS from "crypto-js";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import * as yup from "yup";
import UserRepository from '../../../src/api/repositories/userRepository';
import FirstAid from "../../assets/images/first_aid.png";
import logo from "../../assets/images/logo_svg.svg";
import { formControlStyle } from "../../assets/styled-components/textField";
import { loginSuccess, setHeaderName, setSelectedIndex } from "../../redux/actions/userActions";
import { setTheme } from "../../redux/themes/themeSlice";
import { encryptionKey } from "../../utility/secretKeys";
import { setAuthToken } from "../../utility/tokenUtility";
import { getToken, onMessage } from 'firebase/messaging'; // Import getToken and onMessage
import { messaging } from "../../firebaseConfig";


const registerSchema = yup.object().shape({
  username: yup.string()
    .required('Please enter your Username')
    .max(20, 'Username must be at most 20 characters'),
  password: yup.string()
    .required('Please enter your Password')
    .min(8, 'Password must be at least 8 characters')
    .max(14, 'Password must be at most 14 characters')
    // .matches(
    //   /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^])[A-Za-z\d@$!%*?&#^]+$/,
    //   'Password must be alphanumeric with special characters'
    // ),
});
const LoginCard = styled(Card)`
  background: #fff;
  padding: 30px;
  box-shadow: 0px 2px 20px 0px #dfc6f2bf;
  border-radius: 20px;
  overflow: visible;
  @media screen and (max-width: 900px) {
    padding: 16px;
    width: 75%;
    margin: auto;
  }
  @media screen and (max-width: 600px) {
    padding: 16px;
    width: auto;
  }
`;

const Login = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [hasBlocked, setHasBlocked] = useState(false);

  const { register, handleSubmit, setValue, setFocus, formState: { errors }, trigger } = useForm({
    resolver: yupResolver(registerSchema)
  });

  dispatch(setHeaderName('SET_HEADER_NAME', 'Dashboard'));
  dispatch(setSelectedIndex('SET_INDEX_LABEL', ''));
  useEffect(() => {
    if (errorMessage === "Your Account has been Blocked. Please contact IFAC-X Admin.")
      setHasBlocked(true);
  }, [errorMessage])

  useEffect(() => {
    const savedUsername = localStorage.getItem('savedUsername');
    const savedPassword = localStorage.getItem('savedPassword');
    const rememberMeFlag = localStorage.getItem('rememberMe');

    if (savedUsername && savedPassword && rememberMeFlag !== 'false') {
      try {
        const decryptedPassword = CryptoJS.AES.decrypt(savedPassword, encryptionKey).toString(CryptoJS.enc.Utf8);
        setUserName(savedUsername);
        setPassword(decryptedPassword);
        setRememberMe(true);
        localStorage.setItem('rememberMe', rememberMeFlag);

        // Setting form values with react-hook-form's setValue
        setValue('username', savedUsername);
        setValue('password', decryptedPassword);
      } catch (error) {
        console.error('Decryption error:', error);
        // Handle decryption error (e.g., clear local storage)
        localStorage.removeItem('savedUsername');
        localStorage.removeItem('savedPassword');
        localStorage.removeItem('rememberMe');
      }
    }
    // Focus the username input field when the component mounts
    if (usernameRef.current) {
      usernameRef.current.focus();
    }

    // Add event listener for Enter key
    const handleEnterKeyPress = (event) => {
      if (event.key === 'Enter') {
        if (event.target.id === 'outlined-adornment-password') {
          setValue('password', event.target.value);
          setPassword(event.target.value);
        }
        if (event.target.id === 'username') {
          setValue('username', event.target.value);
          setUserName(event.target.value);
        }
        event.preventDefault();
        handleSubmit(onSubmitHandler)();
      }
    };

    window.addEventListener('keydown', handleEnterKeyPress);

    return () => {
      window.removeEventListener('keydown', handleEnterKeyPress);
    };
  }, [handleSubmit, setValue]);

  const handleCheckboxChange = useCallback(() => {
    setRememberMe((prevRememberMe) => {
      const newRememberMe = !prevRememberMe;
      localStorage.setItem('rememberMe', newRememberMe);
      return newRememberMe;
    });
  }, []);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const getFCMToken = async () => {
    try {      
      const token = await getToken(messaging, { vapidKey: "BFUoFlvygg2EGKlvrqB5HRX_Viodmqe3ZumUJQF6vumDTji2lnAcXzdpmNvEi4QS-kXAGyuooNyZmmgg6NYX0qY" });
      localStorage.setItem('fcmToken', token)
      // Send the token to the backend to save it
    } catch (error) {
      console.error('Error getting token:', error);
    }
  };


  const onSubmitHandler = (data) => {
    loginForm(data);
  };

  const loginForm = async (datas) => {
    try {
      const data = {
        ...datas,
        username: datas?.username.toLowerCase(),
      };
      const response = await UserRepository.validateUser(data);
      if (response.status === 200) {
        getFCMToken();
        setAuthToken(response?.data?.idToken);
        localStorage.setItem('UserName', response?.data?.firstName);
        localStorage.setItem('roleName', response?.data?.roleName);
        localStorage.setItem('roleId', response?.data?.roleId);
        localStorage.setItem('UserId', response?.data?.userId); // Need to fetch the company and facility id from the response
        localStorage.setItem('user_login_status', true);
        localStorage.setItem('theme', response?.data?.theme);
        localStorage.setItem('widgets', response?.data?.widgets);
        localStorage.setItem('LastModified', response?.data?.lastModifiedDate);
        dispatch(setTheme(response?.data?.theme))
        let rememberMeFlag = localStorage.getItem('rememberMe');
        if (rememberMeFlag) {
          const encryptedPassword = CryptoJS.AES.encrypt(datas.password, encryptionKey).toString();
          localStorage.setItem('savedUsername', datas?.username);
          localStorage.setItem('savedPassword', encryptedPassword);
          localStorage.setItem('savedPassword', encryptedPassword);
        } else {
          localStorage.removeItem('savedUsername');
          localStorage.removeItem('savedPassword');
          localStorage.removeItem('rememberMe');
        }
        dispatch(loginSuccess('LOGIN_SUCCESS', response?.data?.idToken));
        if (!response.data?.email || response.data.email.trim() === "") {
          // If email is missing or empty, navigate to the dashboard
          navigate('/emailupdate');
        } else if(response?.data?.firstLoginDate === "") {
          // If email is present, navigate to the welcome page
          navigate('/Expiredchangepassword');
        } else{
          navigate('/welcome');

        }
    
      } else if (response?.status === 401) {
        const wordForUserName = "User";
        const wordForPassword = "password";
        const wordForBlocked = "blocked";

        const regexForPassword = new RegExp(`\\b${wordForPassword}\\b`, 'i'); // 'i' makes it case-insensitive
        const regexForUserName = new RegExp(`\\b${wordForUserName}\\b`, 'i'); // 'i' makes it case-insensitive
        const regexForUserBlock = new RegExp(`\\b${wordForBlocked}\\b`, 'i'); // 'i' makes it case-insensitive

        const isWordPresentForPassword = regexForPassword.test(response?.data?.detail);
        const isWordPresentForUserName = regexForUserName.test(response?.data?.detail);
        const isWordPresentForUserBlock = regexForUserBlock.test(response?.data?.detail);
        if (isWordPresentForUserBlock) {
          setErrorMessage("Your Account has been Blocked. Please contact IFAC-X Admin.");
        }
        else if (isWordPresentForPassword && isWordPresentForUserName) {
          setErrorMessage("Incorrect Credentials");
          trigger('password');
          trigger('username');
        } else if (isWordPresentForUserName) {
          setErrorMessage("Incorrect Username");
          trigger('username');
        } else if (isWordPresentForPassword) {
          // setErrorMessage("Incorrect Password");
          // trigger('password');
          const failedAttempts = response?.data?.detail !== "User account is blocked due to multiple failed login attempts." ? parseInt(response?.data?.detail?.match(/Failed attempts: (\d+)/)[1], 10) : 5;
          setLoginAttempts(failedAttempts);
          setErrorMessage(failedAttempts === 5 ? "Your Account has been Blocked. Please contact IFAC-X Admin." : `Incorrect Password! Only ${5 - failedAttempts} attempts left.`);

        }
      } else if (response?.status >= 500) {
        const failedAttempts = response?.data?.detail !== "User account is blocked due to multiple failed login attempts." ? parseInt(response?.data?.detail?.match(/Failed attempts: (\d+)/)[1], 10) : 5;
        setLoginAttempts(failedAttempts);
        setErrorMessage(failedAttempts === 5 ? "Your Account has been Blocked. Please contact IFAC-X Admin." : `Incorrect Password! Only ${5 - failedAttempts} attempts left.`);
      }else if (response?.status === 400 && response?.data?.detail === "User Is Inactive please contact IFAC-X Administrator.") {
           setErrorMessage("User Is Inactive please contact IFAC-X Administrator.");
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.response && error.response.status === 401) {
        setErrorMessage("Invalid Credentials!");
      } else if (error.response.status >= 500) {

        setErrorMessage("Invalid Credentials!");
      } else {
        setErrorMessage('An error occurred during login');
      }
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (event.target.id === 'username' && passwordRef.current) {
        passwordRef.current.focus();
      } else if (event.target.id === 'password') {
        handleSubmit(onSubmitHandler)();
      }
    }
  };



  const handleFocusChange = (e) => {
    if (errorMessage === 'Incorrect Username') {
      setErrorMessage('');
    }
    if (errorMessage === 'Incorrect Password') {
      setErrorMessage('');
    }
  };
  const handleForgotPassword = (e) => {
    e.preventDefault();
    setErrorMessage("Your Account has been Blocked. Please contact IFAC-X Admin.");
  };
  

  return (
    <Container maxWidth="fixed" component={"section"} className="login" sx={{ display: "flex" }}>
      <Grid container spacing={2} sx={{ paddingInline: { md: 10 } }} className="banner">
        <Grid item xs={12} md={7}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box mb={3} sx={{ textAlign: { xs: "center", md: "start" }, marginTop: { xs: 2, md: 0 } }}>
              <Typography component={"a"} sx={{ display: "inline-block" }}>
                <Typography component={"img"} src={logo}></Typography>
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center", display: { xs: "none", md: "block" } }}>
              <Typography component={"img"} src={FirstAid} sx={{ width: "400px", height: "100%" }}></Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={5}>
          <LoginCard className="round-shade">
            <Typography variant="h1">Login</Typography>
            <Typography variant="h4">Welcome to IFAC-X</Typography>
            <Box sx={{ marginTop: { xs: 2, md: 3 } }}>
              <form onSubmit={handleSubmit(onSubmitHandler)}>
                <FormControl fullWidth sx={{ marginBottom: { xs: 2, md: 3 } }} theme={formControlStyle}>
                  <Typography variant="label" component={"label"} htmlFor="username">
                    Username
                  </Typography>
                  <TextField
                    sx={{ marginTop: 0 }}

                    inputRef={usernameRef} // Attach the ref to the username input field
                    defaultValue={userName}
                    margin="normal"
                    type='text'
                    fullWidth
                    inputProps={{ maxLength: 20 }}
                    id="username"
                    name="username"
                    autoComplete="off"
                    {...register('username')}
                    error={!!errors['username']}
                    helperText={errors['username'] ? errors['username'].message : ''}
                    onKeyDown={handleKeyDown}
                    // onBlur={handleBlurChange}
                    onFocus={handleFocusChange}
                  />
                </FormControl>
                <FormControl
                  fullWidth
                  sx={{ marginBottom: { xs: 2, md: 3 } }}
                  theme={formControlStyle}
                  error={!!errors['password']}
                  variant="outlined"
                >
                  <Typography variant="label" component={"label"} htmlFor="password">
                    Password
                  </Typography>
                  <OutlinedInput
                    inputRef={passwordRef}
                    defaultValue={password}
                    // onBlur={handlePasswordBlurChange}
                    onFocus={handleFocusChange}
                    id="outlined-adornment-password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    error={!!errors['password']}
                    {...register('password')}
                    fullWidth
                    inputProps={{ minlength: 8, maxLength: 14 }}
                    autoComplete="current-password"
                    onKeyDown={handleKeyDown}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {!!errors['password'] && (
                    <FormHelperText error id="outlined-adornment-password">{errors['password'].message}</FormHelperText>
                  )}
                </FormControl>
                {errorMessage && (
                  <Alert severity="error" sx={{ mt: 3, fontSize: '12px', fontWeight: '400', color: '#D31A21', alignItems: 'center' }}>
                    <strong>{errorMessage}</strong>
                  </Alert>
                )}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", marginBottom: { xs: 2, md: 3 } }}>
                  <FormControl sx={{ display: "inline-flex", flexDirection: "row", alignItems: "center" }}>
                    <FormControlLabel control={<Checkbox checked={rememberMe} onChange={handleCheckboxChange} />} />
                    <Typography sx={{ marginLeft: "-12px" }}>
                      Keep me logged In
                    </Typography>
                  </FormControl>
                  <Link component={RouterLink} to={!hasBlocked ? "/forgot-password" : ""}>Forgot Password?</Link>
                </Box>
                <Button fullWidth variant="contained" type="submit">
                  LOGIN
                </Button>
              </form>
            </Box>
          </LoginCard>
        </Grid>
      </Grid>
    </Container>
  );
}
export default Login;